import React, { useMemo } from "react";
import styled from "styled-components/macro";
import {
  BABY_BUTTERFLY_MARGIN,
  PNL_COLORS,
  LOSS_CATEGORIES_STRINGS,
  PROFIT_CATEGORIES_STRINGS,
  LOSS_CATEGORIES_STRINGS_SORTED,
  PROFIT_CATEGORIES_STRINGS_REVERSED,
} from "../../../constants/constants";
import { TREND_CHART_BAR_WIDTH } from "./TrendChartStyles";
import { isEqual } from "lodash";
import { camelCase } from "lodash";
import { useGetPageFromPath } from "utils/customHooks/genericHooks";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import useContainerDimensions from "utils/useContainerDimensions";
import { getScaleFromWidthForLargeScreens } from "pages/DashboardPages/DashboardPagesContent/dashboardPagesContentUtils";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useTrendChartBarRanges } from "utils/customHooks/trendChartHooks";

const TREND_CHART_LOADER_HEIGHT = 500;

const BARSTACK_WIDTH = TREND_CHART_BAR_WIDTH + 10;
const PADDING_HZ = 30;

const PROFIT_MAX_HEIGHT = 60;
const LOSS_MAX_HEIGHT = 15;
const HEIGHT_STEPS = [0.15, 0.05, 0.15, 0.35, 0.45, 0.35];

const ANIMATION_DURATION = 2.8;
const MIN_BARS_FOR_CENTER_ANIMATION = 6;
const MIN_BARS_FOR_SMOOTH_CENTER_ANIMATION = 12;

const TrendChartLoaderSkeleton = ({ className }) => {
  const { isTrendsPage, isHubletPage } = useGetPageFromPath();
  const [flightConfig] = useFlightConfig();
  const shouldHideProfitCategories =
    !isTrendsPage && flightConfig.trendChart.hideProfitCategories;
  const isStorybook = useIsStorybook();
  const [ref, dimensions] = useContainerDimensions();
  const width = dimensions.width || 0;

  const scale = useMemo(
    () =>
      width
        ? getScaleFromWidthForLargeScreens(width * (isHubletPage ? 2 : 1))
        : 1,
    [width, isHubletPage]
  );
  const trendChartBarsRanges = useTrendChartBarRanges();
  const numBars = useMemo(() => {
    // use actual number of bars if available
    const tooManyBars = trendChartBarsRanges?.length > 20;

    const enoughBarsToFillWidth = Math.max(
      0,
      Math.floor(
        (width - 2 * PADDING_HZ) /
          BARSTACK_WIDTH /
          // must be a multiple of 2
          2 /
          Math.max(1, scale)
      ) * 2
    );
    if (tooManyBars) {
      return enoughBarsToFillWidth;
    }
    return (
      trendChartBarsRanges?.length ??
      // else use enough bars to fill the width
      enoughBarsToFillWidth
    );
  }, [trendChartBarsRanges?.length, width, scale]);

  const animationCenterToSides = ({ length, barStackIdx, barIdx }) =>
    -(
      ANIMATION_DURATION /
      (Math.max(
        numBars * Math.ceil(numBars / MIN_BARS_FOR_SMOOTH_CENTER_ANIMATION),
        MIN_BARS_FOR_SMOOTH_CENTER_ANIMATION
      ) *
        length)
    ) *
    (barStackIdx + 1 > numBars / 2 ? numBars - barStackIdx - 1 : barStackIdx) *
    (barIdx + 3.75);

  const animationLeftToRight = ({ length, barStackIdx, barIdx }) =>
    -(ANIMATION_DURATION / (numBars * numBars * length)) *
    barStackIdx *
    (barIdx + 3.75);

  return (
    <div ref={ref} style={{ height: "100%" }}>
      <TrendChartLoaderSkeletonStyles
        $numBars={numBars}
        $shouldHideProfitCategories={shouldHideProfitCategories}
        className={`trendSkeleton ${className}`}
        $isAnimationEnabled={!isStorybook}
      >
        {[...Array(numBars)].map((_, barStackIdx) => (
          <div key={barStackIdx} className="trendChartBar">
            <div className="title">--</div>
            <div className="barStack loading">
              <div className="profit">
                {(shouldHideProfitCategories
                  ? LOSS_CATEGORIES_STRINGS_SORTED
                  : PROFIT_CATEGORIES_STRINGS_REVERSED
                ).map((category, barIdx) => (
                  <div
                    key={category}
                    style={{
                      background: PNL_COLORS[camelCase(category)],
                      animation: `profitGrowY ${ANIMATION_DURATION}s infinite ${
                        numBars < MIN_BARS_FOR_CENTER_ANIMATION
                          ? animationLeftToRight({
                              length: PROFIT_CATEGORIES_STRINGS.length,
                              barStackIdx,
                              barIdx,
                            })
                          : animationCenterToSides({
                              length: PROFIT_CATEGORIES_STRINGS.length,
                              barStackIdx,
                              barIdx,
                            })
                      }s linear`,
                    }}
                    className="bar"
                  />
                ))}
              </div>
              <div className="metric">--</div>
              {shouldHideProfitCategories ? null : (
                <div className="loss">
                  {LOSS_CATEGORIES_STRINGS_SORTED.map((category, barIdx) => (
                    <div
                      key={category}
                      style={{
                        background: PNL_COLORS[camelCase(category)],
                        willChange: "max-height",
                        animation: `lossGrowY ${ANIMATION_DURATION}s infinite ${
                          numBars < MIN_BARS_FOR_CENTER_ANIMATION
                            ? animationLeftToRight({
                                length: LOSS_CATEGORIES_STRINGS.length,
                                barStackIdx,
                                barIdx,
                              })
                            : animationCenterToSides({
                                length: LOSS_CATEGORIES_STRINGS.length,
                                barStackIdx,
                                barIdx,
                              })
                        }s linear`,
                      }}
                      className="bar"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </TrendChartLoaderSkeletonStyles>
    </div>
  );
};
export default React.memo(TrendChartLoaderSkeleton, isEqual);

const makeKeyFrames = maxHeight =>
  HEIGHT_STEPS.reduce(
    (acc, step, idx) => {
      acc += `
  ${(100 * (idx + 1)) / HEIGHT_STEPS.length}% {
    max-height: ${maxHeight * step}px;
    }
    `;
      return acc;
    },
    `0% {
    max-height: ${maxHeight * HEIGHT_STEPS[HEIGHT_STEPS.length - 1]}px;
    }`
  );

const TrendChartLoaderSkeletonStyles = styled.div<{
  $numBars: number;
  $shouldHideProfitCategories: boolean;
  $isAnimationEnabled: boolean;
}>`
  height: ${props =>
    TREND_CHART_LOADER_HEIGHT *
    (props.$shouldHideProfitCategories ? 0.6 : 1)}px;
  height: 100%;
  margin-bottom: -80px;
  padding: ${PADDING_HZ}px ${BABY_BUTTERFLY_MARGIN}px;
  display: flex;
  justify-content: flex-start;
  overflow-x: auto;
  opacity: 0.2;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
  .barStack {
    height: 70%;
    min-width: ${BARSTACK_WIDTH}px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    .metric {
      width: ${TREND_CHART_BAR_WIDTH}px;
      min-height: 40px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .profit,
    .loss {
      .bar {
        width: ${TREND_CHART_BAR_WIDTH}px;
      }
      display: flex;
      flex-direction: column;
    }
    .profit {
      flex-grow: 4;
      justify-content: flex-end;
      .bar {
        max-height: ${PROFIT_MAX_HEIGHT * 0.2}px;
        flex-grow: 1;
      }
    }
    .loss {
      flex-grow: 1;
      justify-content: flex-start;
      .bar {
        max-height: ${LOSS_MAX_HEIGHT * 0.2}px;
        flex-grow: 1;
      }
    }
  }
  &.internetExplorer {
    .barStack {
      height: 100%;
      .profit,
      .loss {
        height: 100%;
      }
      .profit {
        justify-content: flex-start;
        transform: scaleY(-1);
      }
    }
    .metricLabel {
      margin-top: 0.5em;
      margin-bottom: -0.75em;
    }
  }

  @keyframes lossGrowY {
    ${props =>
      props.$isAnimationEnabled ? makeKeyFrames(LOSS_MAX_HEIGHT) : ""}
  }
  @keyframes profitGrowY {
    ${props =>
      props.$isAnimationEnabled ? makeKeyFrames(PROFIT_MAX_HEIGHT) : ""}
  }
`;
